import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPPersona, IPPersonaSize, IPStack, IPText, IPToggle } from 'baseComponents';
import { getCopilotConsentStateSuffix, getCurrentRoute } from 'components/Copilot/Panel/Runtime/copilotConsentReturn';
import TextInfo from 'components/Notification/Info';
import ReportOutputLocationSetup from 'components/Reports/ReportOutputLocationSetup';
import { globalStackTokensGapSmall } from 'globalStyles';
import { CopilotConversationItem } from 'models/copilot/copilotConversation';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceList, { ResourceListType } from 'models/resourceList';
import { SharepointLibraries, SharepointReportsAndPublishing } from 'models/setting';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetSettings, apiSetSetting } from 'services/Api/settingService';
import { AdminConsentParams } from 'services/Auth/adminConsentParams';
import {
  apiClientId,
  apiRequest,
  appClientId,
  graphSharepointLibraryRequest,
  graphSharepointReportsConsent,
  graphSharepointSelectedRequest,
} from 'services/Auth/authConfig';
import { isAuthUserError } from 'services/Auth/authErrors';
import Logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { toLocaleDateTimeShort } from 'utils/datetime';

interface ISharepointIntegrationsMessageProps {
  item: CopilotConversationItem;
}

// Where the admin-consent flow returns to. An app registration only accepts registered redirect URIs, so
// this cannot follow the scene the card was opened on; the copilot panel handles the response on this route
// and then continues on the scene named in the consent state suffix (see consentStateSuffix below).
const consentRedirectUrl = 'library/links';

// What the server requires before it writes a report into the library (mirror of the API's report
// library resolution): the output location setting points at a document library with a drive.
const isUsableOutputLocation = (list: ResourceList | undefined): list is ResourceList => {
  return (
    list !== undefined && list.listId >= 0 && list.listType === ResourceListType.DocumentLibrary && !!list.spDriveId
  );
};

//
// The SharePoint setup a copilot process needs, completed in place: both integration toggles and the
// report output location. When everything is there the card hands over to the process (item.action) -
// on mount only for the card re-opened after a consent redirect (item.actionOnMount), otherwise only
// after the user changed something here, so a refused start can never loop into a restart.
//
export const SharepointIntegrationsMessage = (props: ISharepointIntegrationsMessageProps) => {
  const theme = getTheme();
  const appContext = useContext(AppContext);
  const { t } = useTranslation([
    'copilot',
    'adminCompany',
    'adminReports',
    'outputLocations',
    'translation',
    'identityproviders',
  ]);
  // the toggles grant admin consent, a Microsoft Entra flow: a user of another provider cannot complete the setup here
  const hasMicrosoftIdentity = appContext.hasMicrosoftIdentity();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sharepointLibrariesEnabled, setSharepointLibrariesEnabled] = useState<boolean>(false);
  const [sharepointReportsEnabled, setSharepointReportsEnabled] = useState<boolean>(false);
  const bgColor = appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.themeLighterAlt;

  useEffect(() => {
    loadData(props.item.actionOnMount === true);
    clearConsentResponseFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The consent redirect lands with its response in the query string. Strip it once the card is up so a
  // reload or a context switch does not replay it; leave the rest of the URL alone, the card also shows
  // on scenes that carry their own parameters.
  const clearConsentResponseFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(AdminConsentParams.paramResult) && !params.has(AdminConsentParams.paramError)) return;

    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const loadData = async (startWhenComplete: boolean) => {
    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const allSettings = await apiGetSettings(accessToken);

      const libraries = allSettings.get(SharepointLibraries) === true;
      const reports = allSettings.get(SharepointReportsAndPublishing) === true;

      // Fresh from the API: mirror into the shared cache, which the report settings modal reads. The
      // consent redirect can leave that cache behind the database.
      appContext.globalDataCache.settings.set(SharepointLibraries, libraries);
      appContext.globalDataCache.settings.set(SharepointReportsAndPublishing, reports);

      //the category of the general reports location, read fresh for the same reason
      let list: ResourceList | undefined = undefined;
      if (reports) {
        await appContext.globalDataCache.lists.getItems();
        await appContext.globalDataCache.outputLocations.getItems(true);
        list = appContext.globalDataCache.outputLocations.getList(DocumentOutputPurpose.GeneralReports);
      }

      setSharepointLibrariesEnabled(libraries);
      setSharepointReportsEnabled(reports);

      // Everything the process needs is in place: hand over to it.
      if (startWhenComplete && libraries && reports && isUsableOutputLocation(list) && props.item.action) {
        await props.item.action();
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // The scene and copilot context this card was opened on, so the panel can bring the user back here after the
  // consent round trip and re-open the card in this context.
  const consentStateSuffix = () => getCopilotConsentStateSuffix(appContext.copilotBridge.activeContext, getCurrentRoute());

  const setSharePointLibrary = async (checked: boolean, getPermissions: boolean) => {
    try {
      appContext.showContentLoader();
      setSharepointLibrariesEnabled(checked);

      if (checked && getPermissions) {
        let scopes = graphSharepointLibraryRequest.scopes;
        if (appContext.isSelectedScopeEnabled(appClientId)) {
          scopes = graphSharepointSelectedRequest.scopes;
        }
        appContext.adminConsentScope(appClientId, scopes, consentRedirectUrl, consentStateSuffix());
      } else {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const libraries = await apiSetSetting(SharepointLibraries, checked, accessToken);
        if (libraries.settingValue) {
          appContext.globalDataCache.settings.set(SharepointLibraries, checked);
        }
      }
    } catch (err) {
      handleAuthErrors(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const setSharePointReport = async (checked: boolean, getPermissions: boolean) => {
    try {
      appContext.showContentLoader();
      setSharepointReportsEnabled(checked);
      if (checked && getPermissions) {
        let scopes = graphSharepointReportsConsent.scopes;
        if (appContext.isSelectedScopeEnabled(apiClientId)) {
          scopes = graphSharepointSelectedRequest.scopes;
        }
        appContext.adminConsentScope(apiClientId, scopes, consentRedirectUrl, consentStateSuffix());
      } else {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const reports = await apiSetSetting(SharepointReportsAndPublishing, checked, accessToken);
        if (reports.settingValue) {
          appContext.globalDataCache.settings.set(SharepointReportsAndPublishing, checked);
        }
      }
    } catch (err) {
      handleAuthErrors(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const handleAuthErrors = (err: unknown) => {
    Logger.debug('Consent error', err);

    if (isAuthUserError(err as Error)) {
      return;
    }

    const appError = AppError.fromAuthError(err);

    switch (appError.message.toLowerCase()) {
      case 'item not found': //not an actual error in this context. probably user has no right on any site
        return;
      case 'tenant does not have a spo license.':
        appError.message = t('sharepoint:Errors.NoLicense', { tenant: appContext.user.tenant.name });
    }

    appContext.showNotification(appError.message, true);
  };

  return (
    <IPStack horizontal horizontalAlign="start" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPPersona hidePersonaDetails={true} size={IPPersonaSize.size40} imageInitials={'AI'} initialsColor={'green'} />
      </IPStack.Item>
      <IPStack.Item styles={{ root: { padding: 8, borderRadius: 5, background: bgColor } }}>
        <IPStack tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPText variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
              {toLocaleDateTimeShort(props.item.date)}
            </IPText>
          </IPStack.Item>
          <IPText>{t('copilot:ProcessException.SharepointIntegrations')}</IPText>
          {!hasMicrosoftIdentity && <IPText>{t('identityproviders:Integration.MicrosoftOnly')}</IPText>}
          {hasMicrosoftIdentity && (
            <IPStack.Item>
              <TextInfo message={t('adminCompany:Tabs.Integration.SharepointLibraries.Info')}>
                <IPLabel>{t('adminCompany:Tabs.Integration.SharepointLibraries.Label')}</IPLabel>
              </TextInfo>
              <IPToggle
                checked={sharepointLibrariesEnabled}
                disabled={isLoading}
                onText={t('translation:General.Toggle.On')}
                offText={t('translation:General.Toggle.Off')}
                onChange={(ev, checked) => {
                  setSharePointLibrary(checked ? checked : false, true);
                }}
              />
            </IPStack.Item>
          )}
          {hasMicrosoftIdentity && (
            <IPStack.Item>
              <TextInfo message={t('adminCompany:Tabs.Integration.SharepointReports.Info')}>
                <IPLabel>{t('adminCompany:Tabs.Integration.SharepointReports.Label')}</IPLabel>
              </TextInfo>
              <IPToggle
                checked={sharepointReportsEnabled}
                disabled={isLoading || !sharepointLibrariesEnabled}
                onText={t('translation:General.Toggle.On')}
                offText={t('translation:General.Toggle.Off')}
                onChange={(ev, checked) => {
                  setSharePointReport(checked ? checked : false, true);
                }}
              />
            </IPStack.Item>
          )}
          {hasMicrosoftIdentity && (
            <IPStack.Item>
              <TextInfo message={t('outputLocations:Purpose.GeneralReports.Info')}>
                <IPLabel>{t('copilot:SharepointIntegrationsCard.OutputLocation.Label')}</IPLabel>
              </TextInfo>
              {/* the category is set here, in place: once it is bound the card re-checks and resumes the process */}
              <ReportOutputLocationSetup
                isOpen={true}
                disabled={isLoading || !sharepointReportsEnabled}
                onChanged={() => loadData(true)}
              />
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};
